/* eslint-disable @typescript-eslint/no-explicit-any */

import { chatboxDebugLog } from "@/lib/debug";

interface EmbedConfig {
  apiUrl?: string;
  socketUrl?: string;
  hostOrigin?: string;
  instanceId: string;
  widgetKey: string;
  locale?: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  color?: string;
  primaryColor?: string;
  chatboxName?: string;
  avatar?: string;
  description?: string;
  quickActions?: string[];
  toolCatalogId?: string;
  toolCatalogVersion?: number;
  toolCatalogHash?: string;
  welcomeMessage?: string;
  baseUrl?: string;
}

interface ChatboxWidgetController {
  init: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  destroy: () => void;
}

const DEFAULT_CONFIG: Partial<EmbedConfig> = {
  locale: "vi",
  position: "bottom-right",
  color: "#2563eb",
};

const EMBED_LABELS = {
  vi: {
    openChat: "Mở chat",
    closeChat: "Đóng chat",
  },
  en: {
    openChat: "Open chat",
    closeChat: "Close chat",
  },
} as const;

const IFRAME_ID = "chatbox-widget-iframe";
const RESIZE_DEBOUNCE_MS = 120;

let iframeEl: HTMLIFrameElement | null = null;
let isOpen = false;
let configCache: EmbedConfig | null = null;
let loadHandler: ((this: HTMLIFrameElement, ev: Event) => any) | null = null;
let messageHandler: ((event: MessageEvent) => void) | null = null;
let resizeTimer: number | null = null;
let isInitialized = false;
let initTimeoutId: number | null = null;
const SINGLETON_KEY = "__CHATBOX_WIDGET_CONTROLLER__";

function parseConfig(): EmbedConfig {
  const script = document.currentScript as HTMLScriptElement | null;

  if (!script) {
    throw new Error("[ChatboxWidget] Cannot find current script tag.");
  }

  const get = (key: string): string | undefined => {
    const val = script.getAttribute(`data-${key}`);
    return val ?? undefined;
  };

  const instanceId = get("instance-id");
  const widgetKey = get("widget-key");

  if (!instanceId || !widgetKey) {
    throw new Error(
      "[ChatboxWidget] Missing required data attributes: data-instance-id, data-widget-key",
    );
  }

  return {
    ...DEFAULT_CONFIG,
    instanceId,
    widgetKey,
    locale: get("locale"),
    position: get("position") as EmbedConfig["position"],
    color: get("color") || get("primary-color"),
    apiUrl: get("api-url"),
    socketUrl: get("socket-url"),
    hostOrigin: get("host-origin") || window.location.origin,
    chatboxName: get("chatbox-name"),
    avatar: get("avatar"),
    description: get("description"),
    quickActions: parseQuickActions(get("quick-actions")),
    toolCatalogId: get("tool-catalog-id"),
    toolCatalogVersion: parseToolCatalogVersion(get("tool-catalog-version")),
    toolCatalogHash: get("tool-catalog-hash"),
    welcomeMessage: get("welcome-message"),
    baseUrl: get("base-url") || new URL(script.src).origin,
  };
}

function resolvePositionStyles(
  position: EmbedConfig["position"],
): Partial<Record<string, string>> {
  switch (position) {
    case "bottom-left":
      return { bottom: "24px", left: "24px" };
    case "top-right":
      return { top: "24px", right: "24px" };
    case "top-left":
      return { top: "24px", left: "24px" };
    case "bottom-right":
    default:
      return { bottom: "24px", right: "24px" };
  }
}

function resolveFramePositionStyles(
  position: EmbedConfig["position"],
): Partial<Record<string, string>> {
  switch (position) {
    case "bottom-left":
      return { bottom: "16px", left: "16px" };
    case "top-right":
      return { top: "16px", right: "16px" };
    case "top-left":
      return { top: "16px", left: "16px" };
    case "bottom-right":
    default:
      return { bottom: "16px", right: "16px" };
  }
}

function getResponsiveFrameStyles(
  isExpanded: boolean,
): Partial<CSSStyleDeclaration> {
  const isCompact = window.innerWidth < 640;

  if (!isExpanded) {
    return {
      width: "56px",
      height: "56px",
      maxHeight: "56px",
      borderRadius: "9999px",
    };
  }

  if (isCompact) {
    return {
      width: "calc(100vw - 16px)",
      height: "calc(100vh - 32px)",
      maxHeight: "calc(100vh - 32px)",
      borderRadius: "12px",
    };
  }

  return {
    width: "min(432px, calc(100vw - 32px))",
    height: "min(720px, calc(100vh - 32px))",
    maxHeight: "calc(100vh - 32px)",
    borderRadius: "16px",
  };
}

function applyFrameStyles(iframe: HTMLIFrameElement, config: EmbedConfig) {
  const pos = resolveFramePositionStyles(config.position);
  const responsive = getResponsiveFrameStyles(isOpen);

  Object.assign(iframe.style, {
    position: "fixed",
    zIndex: "2147483647",
    border: "none",
    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
    background: "transparent",
    transition: "transform 200ms ease, opacity 200ms ease",
    transform: "scale(1)",
    opacity: "1",
    pointerEvents: "auto",
    display: "block",
    overflow: "hidden",
    ...pos,
    ...responsive,
  });
}

function getEmbedUrl(config: EmbedConfig): URL {
  const url = new URL("/embed", config.baseUrl || window.location.origin);
  url.searchParams.set("config", encodeRuntimeConfig(config));
  url.searchParams.set("view", "panel");
  return url;
}

function createIframe(config: EmbedConfig): HTMLIFrameElement {
  const existing = document.getElementById(IFRAME_ID);
  if (existing) existing.remove();

  const iframe = document.createElement("iframe");
  iframe.id = IFRAME_ID;
  iframe.title = "Chatbox Widget";
  iframe.setAttribute("allow", "microphone");
  iframe.setAttribute("allowTransparency", "true");
  iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
  iframe.setAttribute("loading", "eager");

  applyFrameStyles(iframe, config);
  iframe.src = getEmbedUrl(config).toString();

  document.body.appendChild(iframe);
  iframeEl = iframe;
  return iframe;
}

function showIframe(config: EmbedConfig) {
  if (!iframeEl) return;

  isOpen = true;
  applyFrameStyles(iframeEl, config);
}

function hideIframe() {
  if (!iframeEl) return;

  isOpen = false;
  applyFrameStyles(iframeEl, configCache || ({} as EmbedConfig));
}

function attachIframeLoadHandler(config: EmbedConfig) {
  if (!iframeEl) return;

  detachIframeLoadHandler();
  loadHandler = () => {
    if (!iframeEl?.contentWindow) return;

    chatboxDebugLog("host posting CHATBOX_CONFIG", {
      targetOrigin: new URL(config.baseUrl || window.location.origin).origin,
      instanceId: config.instanceId,
      locale: config.locale,
      position: config.position,
    });
    iframeEl.contentWindow.postMessage(
      { type: "CHATBOX_CONFIG", payload: config },
      new URL(config.baseUrl || window.location.origin).origin,
    );
  };

  iframeEl.addEventListener("load", loadHandler);
}

function detachIframeLoadHandler() {
  if (iframeEl && loadHandler) {
    iframeEl.removeEventListener("load", loadHandler);
  }
  loadHandler = null;
}

function attachMessageHandler() {
  detachMessageHandler();

  messageHandler = (event: MessageEvent) => {
    if (event.source !== iframeEl?.contentWindow) return;
    if (event.data?.type === "CHATBOX_READY") {
      chatboxDebugLog("host received CHATBOX_READY", { origin: event.origin });
      clearInitTimeout();
    }
    if (event.data?.type === "CHATBOX_CLOSE") {
      chatboxDebugLog("host received CHATBOX_CLOSE", { origin: event.origin });
      close();
    }
    if (event.data?.type === "CHATBOX_OPEN") {
      chatboxDebugLog("host received CHATBOX_OPEN", { origin: event.origin });
      open();
    }
    if (event.data?.type === "CHATBOX_INIT_ERROR") {
      chatboxDebugLog("host received CHATBOX_INIT_ERROR", {
        origin: event.origin,
      });
      clearInitTimeout();
      destroy();
    }
  };

  window.addEventListener("message", messageHandler);
}

function detachMessageHandler() {
  if (messageHandler) {
    window.removeEventListener("message", messageHandler);
  }
  messageHandler = null;
}

function onResize() {
  if (resizeTimer !== null) {
    clearTimeout(resizeTimer);
  }

  resizeTimer = window.setTimeout(() => {
    resizeTimer = null;
    if (iframeEl && configCache) {
      applyFrameStyles(iframeEl, configCache);
    }
  }, RESIZE_DEBOUNCE_MS);
}

function attachInitTimeout() {
  clearInitTimeout();
  initTimeoutId = window.setTimeout(() => {
    chatboxDebugLog(
      "host init timeout — no CHATBOX_READY or CHATBOX_INIT_ERROR received",
    );
    destroy();
  }, 10000);
}

function clearInitTimeout() {
  if (initTimeoutId !== null) {
    clearTimeout(initTimeoutId);
    initTimeoutId = null;
  }
}

function init() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (isInitialized) return;

  try {
    const globalWindow = window as any;
    const previousController = globalWindow[SINGLETON_KEY] as
      | ChatboxWidgetController
      | undefined;
    if (
      previousController &&
      previousController.destroy &&
      previousController !== controller
    ) {
      previousController.destroy();
    }

    const config = parseConfig();
    configCache = config;

    isOpen = false;
    iframeEl = createIframe(config);
    attachIframeLoadHandler(config);
    attachMessageHandler();
    attachInitTimeout();
    window.addEventListener("resize", onResize, { passive: true });
    globalWindow[SINGLETON_KEY] = controller;
    isInitialized = true;
  } catch (err: any) {
    destroy();
    console.error("[ChatboxWidget] Init failed:", err.message);
  }
}

function open() {
  if (!configCache) {
    init();
  }
  if (!configCache) return;
  if (!iframeEl) {
    iframeEl = createIframe(configCache);
    attachIframeLoadHandler(configCache);
  }
  isOpen = true;
  showIframe(configCache);
}

function close() {
  hideIframe();
}

function destroy() {
  isOpen = false;
  clearInitTimeout();

  if (resizeTimer !== null) {
    clearTimeout(resizeTimer);
    resizeTimer = null;
  }

  detachIframeLoadHandler();
  detachMessageHandler();
  window.removeEventListener("resize", onResize);

  if (iframeEl) {
    iframeEl.remove();
    iframeEl = null;
  }

  configCache = null;

  const globalWindow = window as any;
  if (globalWindow[SINGLETON_KEY] === controller) {
    delete globalWindow[SINGLETON_KEY];
  }

  isInitialized = false;
}

function encodeRuntimeConfig(config: EmbedConfig): string {
  const payload = JSON.stringify(config);
  return btoa(unescape(encodeURIComponent(payload)));
}

function getEmbedLabels(locale?: string | null) {
  const normalized = locale?.toLowerCase().startsWith("en") ? "en" : "vi";
  return EMBED_LABELS[normalized];
}

function parseQuickActions(value?: string): string[] | undefined {
  if (typeof value === "undefined") return undefined;
  if (!value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
        .slice(0, 4);
    }
  } catch {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 4);
  }

  return undefined;
}

function parseToolCatalogVersion(value?: string): number | undefined {
  if (!value) return undefined;

  const parsed = Number(value.trim());
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

function toggle() {
  if (isOpen) {
    close();
  } else {
    open();
  }
}

const controller: ChatboxWidgetController = {
  init,
  open,
  close,
  toggle,
  destroy,
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

(window as any).ChatboxWidget = controller;
